import * as url from "../../helpers/url_helper";
import { apiCall, fetchApi } from "./apiService";
import { handleApiError } from "./helpers";

/**
 * Fetches merchant info by shop domain
 * @param {string} shop - Shop domain
 * @param {Function} notifyError - Error notification function
 * @returns {Promise<Object|null>} Merchant data or null if error
 */
export const fetchMerchantInfo = async (shop, notifyError) => {
    return await apiCall(
        `${url.MERCHANT_INFO}?store=${encodeURIComponent(shop)}`,
        'GET',
        null,
        notifyError,
        "Failed to load merchant info"
    );
};

/**
 * Fetches orders count for a merchant
 * @param {string} shop - Shop domain
 * @param {string} fromDate - Start date for period
 * @param {Function} notifyError - Error notification function
 * @returns {Promise<number|null>} Orders count or null if error
 */
export const fetchOrdersCount = async (shop, fromDate, notifyError) => {
    try {
        if (!fromDate) return null;

        const { response, results } = await fetchApi(`${url.GET_ORDERS_COUNT}?shop=${shop}&from_date=${fromDate}`, 'GET');

        if (response.ok && results?.status && results?.data !== undefined) {
            return parseInt(results.data);
        }
        return null;
    } catch (err) {
        notifyError("Failed to load orders count. Please check your network connection.");
        return null;
    }
};

/**
 * Fetches merchants revenue/filter data
 * @param {string} shop - Shop domain
 * @param {string} filterType - Filter type (ALL, THIS_MONTH, etc.)
 * @param {Function} notifyError - Error notification function
 * @returns {Promise<Object|null>} Revenue data or null if error
 */
export const fetchMerchantsDataFilter = async (shop, filterType = 'ALL', notifyError) => {
    return await apiCall(
        `${url.GET_MERCHANTS_DATA_FILTER}?shop=${shop}&filterType=${filterType}`,
        'GET',
        null,
        notifyError,
        "Failed to load merchants data"
    );
};

/**
 * Fetches coupon codes for a merchant
 * @param {string} shop - Shop domain
 * @param {Function} notifyError - Error notification function
 * @returns {Promise<Array|null>} Array of coupons or null if error
 */
export const fetchCouponCodes = async (shop, notifyError) => {
    return await apiCall(
        `${url.GET_COUPON_CODE}?shop=${shop}`,
        'GET',
        null,
        notifyError,
        "Failed to load coupon codes"
    );
};

/**
 * Updates coupon code status
 * @param {string} shop - Shop domain
 * @param {string} id - Coupon ID
 * @param {string} status - New status (active/deactive)
 * @param {Function} notifyError - Error notification function
 * @returns {Promise<boolean>} True if successful, false otherwise
 */
export const updateCouponCodeStatus = async (shop, id, status, notifyError) => {
    try {
        const { response, results } = await fetchApi(url.PUT_COUPON_CODE, 'PUT', {
            shop,
            id,
            status
        });

        if (response.ok && results?.status) {
            return true;
        } else {
            handleApiError(response, results, notifyError);
            return false;
        }
    } catch (err) {
        notifyError("Failed to update coupon code status. Please check your network connection.");
        return false;
    }
};

/**
 * Fetches blocked orders for a merchant
 * @param {string} shop - Shop domain
 * @param {Function} notifyError - Error notification function
 * @returns {Promise<Array|null>} Array of blocked orders or null if error
 */
export const fetchBlockedOrders = async (shop, notifyError) => {
    return await apiCall(
        `${url.GET_BLOCKED_ORDERS}?shop=${shop}`,
        'GET',
        null,
        notifyError,
        "Failed to load blocked orders"
    );
};

/**
 * Updates product metafield
 * @param {string} shop - Shop domain
 * @param {string} metafieldKey - Metafield key
 * @param {string} metafieldValue - Metafield value
 * @param {Function} notifyError - Error notification function
 * @returns {Promise<boolean>} True if successful, false otherwise
 */
export const updateProductMetafield = async (shop, metafieldKey, metafieldValue, notifyError) => {
    try {
        const { response, results } = await fetchApi(url.PUT_PRODUCTS, 'PUT', {
            shop,
            metafield_key: metafieldKey,
            metafield_value: metafieldValue
        });

        if (response.ok) {
            return true;
        } else {
            handleApiError(response, results, notifyError);
            return false;
        }
    } catch (err) {
        notifyError("Failed to update product metafield. Please check your network connection.");
        return false;
    }
};

/**
 * Updates store collections metafield
 * @param {string} shop - Shop domain
 * @param {string} metafieldKey - Metafield key
 * @param {string} metafieldValue - Metafield value
 * @param {Function} notifyError - Error notification function
 * @returns {Promise<boolean>} True if successful, false otherwise
 */
export const updateStoreCollectionsMetafield = async (shop, metafieldKey, metafieldValue, notifyError) => {
    try {
        const { response, results } = await fetchApi(url.PUT_STORE_COLLECTIONS, 'PUT', {
            shop,
            metafield_key: metafieldKey,
            metafield_value: metafieldValue
        });

        if (response.ok) {
            return true;
        } else {
            handleApiError(response, results, notifyError);
            return false;
        }
    } catch (err) {
        notifyError("Failed to update store collections metafield. Please check your network connection.");
        return false;
    }
};

